import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { operatorRequiresValue } from '../filters/operators';
import FilterService, { isValidFilterValue } from '../filters/FilterService';

/**
 * ActiveFiltersBar Component
 * Displays active filters as chips with close icons below the toolbar
 */
function ActiveFiltersBar({
  configss = [],
  module = null,
  /** Labels from last list API response: { "project:10": "Proleed Academy - ..." } */
  apiResolvedFilterLabels = {},
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const safeConfigss = Array.isArray(configss) ? configss : [];
  const [fkValueToLabel, setFkValueToLabel] = useState({});
  const [metaLabelByField, setMetaLabelByField] = useState({});

  // Get operator labels mapping
  const operatorLabels = {
    'equals': '=',
    'not_equals': '≠',
    'contains': 'contains',
    'not_contains': 'not contains',
    'starts_with': 'starts with',
    'ends_with': 'ends with',
    'greater_than': '>',
    'greater_than_or_equal': '≥',
    'less_than': '<',
    'less_than_or_equal': '≤',
    'in': 'in',
    'not_in': 'not in',
    'between': 'between',
    'not_between': 'not between',
    'is_null': 'is null',
    'is_not_null': 'is not null',
    'today': 'is today',
    'this_week': '-This week',
    'this_month': '-This month',
    'on': 'on',
    'date_equals': '=',
    'before': 'before',
    'date_before': 'before',
    'after': 'after',
    'date_after': 'after',
  };

  // Extract active filters from URL params
  const activeFilters = useMemo(() => {
    const filters = [];
    const processedFields = new Set();

    searchParams.forEach((value, key) => {
      // Skip operator/label params - they'll be processed with their field
      if (key.endsWith("_operator") || key.endsWith("_label")) return;

      if (key.startsWith("filter_")) {
        const field = key.replace("filter_", "");

        // Skip if already processed
        if (processedFields.has(field)) return;
        processedFields.add(field);

        // Get operator from URL
        const operatorKey = `filter_${field}_operator`;
        const operator = searchParams.get(operatorKey) || 'contains';
        const requiresValue = operatorRequiresValue(operator);

        // For operators that don't require values, show filter even if value is empty
        // For operators that require values, only show if value is valid (not "undefined" placeholder)
        if (!requiresValue || isValidFilterValue(value)) {
          // Get field label from configss
          const fieldConfig = safeConfigss.find(col => col.field === field);
          const fieldLabel = fieldConfig?.label || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

          // Raw filter value from URL (IDs for FK fields). Labels are resolved later from lookup map.
          const rawValue = value || '';

          filters.push({
            field,
            fieldLabel,
            operator,
            rawValue,
            operatorKey,
          });
        }
      }
    });

    return filters;
  }, [searchParams, safeConfigss]);

  // Build a value->label map for FK filters so chips show human-readable labels
  useEffect(() => {
    let isCancelled = false;

    const buildLookupMap = async () => {
      try {
        if (!module || activeFilters.length === 0) {
          setFkValueToLabel({});
          setMetaLabelByField({});
          return;
        }

        const metaRes = await FilterService.getFilterMetadata(module);
        if (!metaRes?.success || !Array.isArray(metaRes.fields)) {
          setFkValueToLabel({});
          setMetaLabelByField({});
          return;
        }

        const fieldsByKey = new Map(metaRes.fields.map((f) => [f.field_key, f]));
        const nextMetaLabels = {};
        metaRes.fields.forEach((f) => {
          if (f?.field_key && f?.label) nextMetaLabels[f.field_key] = f.label;
        });
        const nextMap = {};

        await Promise.all(
          activeFilters.map(async (f) => {
            const meta = fieldsByKey.get(f.field);
            if (!meta || meta.field_type !== 'foreign_key' || !meta.ui_options_api) return;

            // Fetch lookup options and build mapping from id -> label
            const opts = await FilterService.fetchLookupOptions(meta.ui_options_api, {
              valueField: FilterService.resolveMetadataFkValueField(f.field, meta),
              labelField: FilterService.resolveMetadataFkLabelField(f.field, meta),
              contextParams: metaRes.list_config?.lookupContext || {},
            });
            if (!Array.isArray(opts)) return;

            opts.forEach((o) => {
              const v = o?.value;
              const label = o?.label;
              if (v === null || v === undefined) return;
              if (label === null || label === undefined) return;
              nextMap[`${f.field}:${String(v)}`] = String(label);
            });
          })
        );

        if (!isCancelled) {
          setFkValueToLabel(nextMap);
          setMetaLabelByField(nextMetaLabels);
        }
      } catch (e) {
        if (!isCancelled) {
          setFkValueToLabel({});
          setMetaLabelByField({});
        }
      }
    };

    buildLookupMap();
    return () => {
      isCancelled = true;
    };
  }, [module, activeFilters]);

  // Handle removing a single filter
  const handleRemoveFilter = (field, operatorKey) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(`filter_${field}`);
    newSearchParams.delete(`filter_${field}_label`);
    newSearchParams.delete(`filter_${field}_logic`);
    if (newSearchParams.has(operatorKey)) {
      newSearchParams.delete(operatorKey);
    }
    setSearchParams(newSearchParams, { replace: true });
  };

  // Handle removing all filters
  const handleClearAllFilters = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_")) {
        newSearchParams.delete(key);
      }
    });
    setSearchParams(newSearchParams, { replace: true });
  };

  // Don't render if no active filters
  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={1}
      sx={{
        mb: 2,
        p: 1.5,
        backgroundColor: '#fafafa',
        
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            fontWeight: 500,
            mr: 1,
            fontSize: '0.875rem',
          }}
        >
          Active Filters:
        </Typography>
        {activeFilters.map((filter, index) => {
          // For operators that don't require values, don't show the value
          const requiresValue = operatorRequiresValue(filter.operator);

          // Resolve FK id(s) to label(s) from lookup metadata (ignore stale URL _label params)
          let displayValue = filter.rawValue || '';
          if (requiresValue && displayValue) {
            const tokens = String(displayValue)
              .split(',')
              .map((t) => t.trim())
              .filter(Boolean);
            const resolved = tokens.map((t, tokenIdx) => {
              const mapKey = `${filter.field}:${t}`;
              const fromApi = apiResolvedFilterLabels[mapKey];
              if (fromApi) return fromApi;

              const fromLookup = fkValueToLabel[mapKey];
              if (fromLookup) return fromLookup;

              // Stale URL label — only when nothing else resolved this id
              const labelOverride = searchParams.get(`filter_${filter.field}_label`);
              if (labelOverride) {
                const overrideParts = labelOverride.split(',').map((p) => p.trim());
                if (overrideParts[tokenIdx]) return overrideParts[tokenIdx];
                if (tokens.length === 1 && overrideParts[0]) return overrideParts[0];
              }
              return t;
            });
            displayValue = resolved.join(', ');
          }

          const effectiveFieldLabel = metaLabelByField[filter.field] || filter.fieldLabel;
          const labelText = requiresValue 
            ? `${effectiveFieldLabel} ${operatorLabels[filter.operator] || filter.operator} ${displayValue}`
            : `${effectiveFieldLabel} ${operatorLabels[filter.operator] || filter.operator}`;
          
          return (
          <Chip
            key={`${filter.field}-${index}`}
            label={labelText}
            onDelete={() => handleRemoveFilter(filter.field, filter.operatorKey)}
            deleteIcon={<X size={16} />}
            sx={{
              backgroundColor: '#f5f5f5',
              color: 'text.primary',
              border:'1px solid #bcbcbc',
              '& .MuiChip-deleteIcon': {
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                },
              },
              '& .MuiChip-label': {
                px: 1.5,
                py: 0.5,
              },
            }}
          />
          );
        })}
        {activeFilters.length > 1 && (
          <Chip
            label="Clear All"
            onClick={handleClearAllFilters}
            sx={{
              backgroundColor: 'transparent',
              color: 'primary.main',
              border: '1px solid',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
              },
            }}
          />
        )}
      </Box>
    </Paper>
  );
}

export default ActiveFiltersBar;

